import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { LogOut, FileText, Users, Truck, RefreshCw, Handshake, Briefcase, Database } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminQuotesTab from './AdminQuotesTab';
import AdminApplicationsTab from './AdminApplicationsTab';
import AdminDeliveries from './AdminDeliveries';
import AdminPartnersTab from './AdminPartnersTab';
import AdminCustomersTab from './AdminCustomersTab';
import AdminDriversTab from './AdminDriversTab';
import AdminJobsTab from './AdminJobsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { admin, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({
    total: 0, pending: 0, contacted: 0, completed: 0, cancelled: 0,
    applications: { total: 0, new: 0, reviewed: 0, contacted: 0, hired: 0, rejected: 0 },
    partners: { total: 0, new: 0, contacted: 0, negotiating: 0, active: 0, declined: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [convertQuote, setConvertQuote] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [quotesRes, appsRes, partnersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { withCredentials: true }),
        axios.get(`${API}/admin/applications`, { withCredentials: true }),
        axios.get(`${API}/admin/partners`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true })
      ]);
      setQuotes(quotesRes.data);
      setApplications(appsRes.data);
      setPartners(partnersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateQuoteStatus = async (quoteId, newStatus) => {
    const oldStatus = quotes.find(q => q.id === quoteId)?.status;
    if (!oldStatus || oldStatus === newStatus) return;
    try {
      await axios.put(`${API}/admin/quotes/${quoteId}`, { status: newStatus }, { withCredentials: true });
      setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
      setStats(prev => ({ ...prev, [oldStatus]: Math.max(0, prev[oldStatus] - 1), [newStatus]: prev[newStatus] + 1 }));
    } catch (error) {
      console.error('Failed to update quote status:', error);
    }
  };

  const deleteQuote = async (quoteId) => {
    try {
      const quote = quotes.find(q => q.id === quoteId);
      await axios.delete(`${API}/admin/quotes/${quoteId}`, { withCredentials: true });
      setQuotes(prev => prev.filter(q => q.id !== quoteId));
      setStats(prev => ({ ...prev, total: prev.total - 1, [quote.status]: Math.max(0, prev[quote.status] - 1) }));
    } catch (error) {
      console.error('Failed to delete quote:', error);
    }
  };

  const updateAppStatus = async (appId, newStatus) => {
    const oldStatus = applications.find(a => a.id === appId)?.status;
    if (!oldStatus || oldStatus === newStatus) return;
    try {
      await axios.put(`${API}/admin/applications/${appId}`, { status: newStatus }, { withCredentials: true });
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
      setStats(prev => ({
        ...prev, applications: { ...prev.applications, [oldStatus]: Math.max(0, prev.applications[oldStatus] - 1), [newStatus]: prev.applications[newStatus] + 1 }
      }));
    } catch (error) {
      console.error('Failed to update application status:', error);
    }
  };

  const deleteApplication = async (appId) => {
    try {
      const app = applications.find(a => a.id === appId);
      await axios.delete(`${API}/admin/applications/${appId}`, { withCredentials: true });
      setApplications(prev => prev.filter(a => a.id !== appId));
      setStats(prev => ({
        ...prev, applications: { ...prev.applications, total: prev.applications.total - 1, [app.status]: Math.max(0, prev.applications[app.status] - 1) }
      }));
    } catch (error) {
      console.error('Failed to delete application:', error);
    }
  };

  const handleConvertToDelivery = (quote) => {
    setConvertQuote(quote);
    setActiveTab('deliveries');
  };

  const updatePartnerStatus = async (partnerId, newStatus) => {
    const oldStatus = partners.find(p => p.id === partnerId)?.status;
    if (!oldStatus || oldStatus === newStatus) return;
    try {
      await axios.put(`${API}/admin/partners/${partnerId}`, { status: newStatus }, { withCredentials: true });
      setPartners(prev => prev.map(p => p.id === partnerId ? { ...p, status: newStatus } : p));
      setStats(prev => ({
        ...prev, partners: { ...prev.partners, [oldStatus]: Math.max(0, prev.partners[oldStatus] - 1), [newStatus]: prev.partners[newStatus] + 1 }
      }));
    } catch (error) {
      console.error('Failed to update partner status:', error);
    }
  };

  const deletePartner = async (partnerId) => {
    try {
      const partner = partners.find(p => p.id === partnerId);
      await axios.delete(`${API}/admin/partners/${partnerId}`, { withCredentials: true });
      setPartners(prev => prev.filter(p => p.id !== partnerId));
      setStats(prev => ({
        ...prev, partners: { ...prev.partners, total: prev.partners.total - 1, [partner.status]: Math.max(0, prev.partners[partner.status] - 1) }
      }));
    } catch (error) {
      console.error('Failed to delete partner:', error);
    }
  };

  const appStats = stats.applications || { total: 0, new: 0, reviewed: 0, contacted: 0, hired: 0, rejected: 0 };
  const partnerStats = stats.partners || { total: 0, new: 0, contacted: 0, negotiating: 0, active: 0, declined: 0 };

  return (
    <div className="min-h-screen bg-brand-navy" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-brand-surface border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <img src="https://customer-assets.emergentagent.com/job_683f51e3-42f3-4f5b-90d7-b3d8273b71f6/artifacts/v8u8bxfl_2.png" alt="TopFleet Logistics" className="h-10 w-auto" />
              <span className="text-white font-heading text-lg hidden sm:block">ADMIN PANEL</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="view-site-link">View Site</Link>
              <span className="text-slate-500 text-sm hidden sm:block">{admin?.email}</span>
              <button onClick={() => logout()} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="logout-button">
                <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex items-center gap-2 mb-8 border-b border-white/10 pb-4 overflow-x-auto" data-testid="admin-tabs">
          {[
            { key: 'quotes', label: 'QUOTES', icon: FileText, count: stats.total },
            { key: 'applications', label: 'APPLICATIONS', icon: Users, count: appStats.total },
            { key: 'deliveries', label: 'DELIVERIES', icon: Truck, count: null },
            { key: 'partners', label: 'PARTNERS', icon: Handshake, count: partnerStats.total },
            { key: 'customers', label: 'CUSTOMERS', icon: Database, count: null },
            { key: 'drivers', label: 'DRIVERS', icon: Truck, count: null },
            { key: 'jobs', label: 'JOBS', icon: Briefcase, count: null },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-heading text-sm tracking-wide transition-all ${
                activeTab === tab.key ? 'bg-brand-red text-white' : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'
              }`}
              data-testid={`tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === tab.key ? 'bg-white/20' : 'bg-white/10'}`}>{tab.count}</span>}
            </button>
          ))}
          <div className="ml-auto">
            <button onClick={fetchData} className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-white transition-colors" data-testid="refresh-button">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'quotes' && (
          <AdminQuotesTab quotes={quotes} stats={stats} loading={loading}
            onUpdateStatus={updateQuoteStatus} onDelete={deleteQuote} onConvertToDelivery={handleConvertToDelivery} />
        )}
        {activeTab === 'applications' && (
          <AdminApplicationsTab applications={applications} stats={appStats} loading={loading}
            onUpdateStatus={updateAppStatus} onDelete={deleteApplication} />
        )}
        {activeTab === 'deliveries' && (
          <AdminDeliveries convertQuote={convertQuote} onClearConvert={() => setConvertQuote(null)} />
        )}
        {activeTab === 'partners' && (
          <AdminPartnersTab partners={partners} stats={partnerStats} loading={loading}
            onUpdateStatus={updatePartnerStatus} onDelete={deletePartner} />
        )}
        {activeTab === 'customers' && <AdminCustomersTab />}
        {activeTab === 'drivers' && <AdminDriversTab />}
        {activeTab === 'jobs' && <AdminJobsTab />}
      </main>
    </div>
  );
};

export default AdminDashboard;
